import { motion } from "framer-motion";
import { ShieldCheck, Zap, Layers, Repeat, TrendingUp } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 56 — Photographers are safe. Expectations will change.
 */

const shifts = [
  { icon: Zap, label: "Faster turnaround" },
  { icon: TrendingUp, label: "Higher output" },
  { icon: Layers, label: "More formats" },
  { icon: Repeat, label: "More value per shoot" },
];

export default function Scene56_PhotographersSafe() {
  return (
    <SceneShell variant="ink" align="left" data-testid="scene-photographers-safe">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 30% 60%, rgba(150,193,31,0.10) 0%, transparent 55%)",
            "radial-gradient(ellipse at 30% 60%, rgba(150,193,31,0.16) 0%, transparent 55%)",
            "radial-gradient(ellipse at 30% 60%, rgba(150,193,31,0.10) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mb-6 flex items-center gap-3"
        >
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#96C11F" }} />
          The human side
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-4xl sm:text-6xl lg:text-7xl text-white leading-[1.0]"
          data-testid="photographers-safe-headline"
        >
          Photographers
          <br />
          are{" "}
          <span style={{ color: "#96C11F", fontWeight: 900 }}>safe.</span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-6 text-xl sm:text-2xl"
          style={{ color: "rgba(255,255,255,0.78)" }}
        >
          But the <span style={{ color: "#96C11F", fontWeight: 700 }}>client expectations</span>{" "}
          will change.
        </motion.div>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl">
          {shifts.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 1.6 + i * 0.16,
                  duration: 0.7,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="rounded-2xl glass p-5 flex items-start gap-3"
                style={{
                  background: "rgba(150,193,31,0.07)",
                  border: "1px solid rgba(150,193,31,0.30)",
                }}
                data-testid={`photo-shift-${i}`}
              >
                <Icon
                  className="w-5 h-5 shrink-0 mt-0.5"
                  style={{ color: "#96C11F" }}
                />
                <span
                  className="text-sm sm:text-base whitespace-nowrap"
                  style={{ color: "rgba(255,255,255,0.92)", fontWeight: 600 }}
                >
                  {s.label}
                </span>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 2.6 }}
          className="mt-10 text-xl sm:text-2xl"
          style={{ color: "rgba(255,255,255,0.85)" }}
        >
          And that's exactly what{" "}
          <span
            style={{
              background:
                "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              fontWeight: 800,
            }}
          >
            this system enables.
          </span>
        </motion.div>
      </div>
    </SceneShell>
  );
}
